/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테마 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/theme/Theme.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 28.
 */
import * as parent from '@components/form/field/Field';
import { Language } from '@utils/Language';
import { Select } from '../select/Select';
import { Set } from '../set/Set';
import { Remote } from '@models/store/list/Remote';
import { Local } from '@models/store/list/Local';
import { Record } from '@models/store/Record';
import { Text } from '../text/Text';
import { Color } from '../color/Color';
import './Theme.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 테마목록을 불러올 URL
     */
    url?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Theme extends parent.Field {
    /**
     * 기본 URL
     */
    static url: string = null;

    /**
     * 컴포넌트 확장타입
     */
    variant = 'theme';

    /**
     * 목록을 불러올 URL
     */
    url: string;

    /**
     * 목록필드
     */
    select: Select = null;

    /**
     * 환경설정 필드셋
     */
    config: Set = null;

    /**
     * change 리스너 완료 대기 프라이빗 큐
     */
    #changeReady: Promise<void> = Promise.resolve();
    #resolveChangeReady: (() => void) | null = null;

    /**
     * Display 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Theme> = null) {
        super(properties);

        this.url = properties.url ?? Theme.url ?? '/';
    }

    /**
     * 기본 URL 을 설정한다.
     *
     * @param url - 기본 URL
     */
    static setUrl(url: string): void {
        Theme.url = url;
    }

    /**
     * 목록필드를 가져온다.
     *
     * @returns 목록필드
     */
    getSelect(): Select {
        if (this.select === null) {
            this.select = new Select({
                store: new Remote({
                    provider: {
                        url: this.url,
                    },
                }),
                displayField: 'title',
                valueField: 'pathname',
                flex: 1,
                listRenderer: (_, record) => {
                    return `<div class="screenshot" style="background-image:url(${record.get('screenshot')});"></div><div class="meta"><b>${record.get('title')}</b><small>${record.get('base')}</small></div>`;
                },
                listeners: {
                    change: async (me) => {
                        this.#changeReady = new Promise<void>((resolve) => {
                            this.#resolveChangeReady = resolve;
                        });

                        me.setDisabled(true);
                        const record = (await me.getValueToRecord()) as Record;
                        const config = record?.get('config') ?? null;
                        if (config?.length > 0) {
                            this.getConfig().show();
                            await this.setConfigFields(config);
                        } else {
                            this.getConfig().hide();
                        }
                        me.setDisabled(false);

                        this.#resolveChangeReady?.();
                        this.#resolveChangeReady = null;
                    },
                },
            });
            this.select.setParent(this);
        }

        return this.select;
    }

    /**
     * 추가 환경설정을 위한 필드셋을 가져온다.
     *
     * @returns 환경설정 필드셋
     */
    getConfig(): Set {
        if (this.config === null) {
            this.config = new Set({
                label: Language.get('texts.config'),
                inputName: this.name + '_config',
                hidden: true,
            });
            this.config.setParent(this);
        }

        return this.config;
    }

    /**
     * 환경설정 필드셋에 필드를 추가한다.
     *
     * @param config - 환경설정
     */
    async setConfigFields(config: { [key: string]: any }[]): Promise<void> {
        this.getConfig().removeItems();

        const fields = await this.getConfigFields(config);
        for (const field of fields) {
            this.getConfig().appendItem(field);
        }
    }

    /**
     * 필드설정을 이용하여 필드를 가져온다.
     *
     * @param config - 환경설정
     * @returns 환경설정 필드
     */
    async getConfigFields(config: { [key: string]: any }[]): Promise<parent.Field[]> {
        const fields: parent.Field[] = [];
        for (const item of config) {
            let field: parent.Field = null;
            switch (item.type) {
                case 'fieldset':
                    field = new Set({
                        label: item.label ?? 'UNKNOWN',
                        items: [...(await this.getConfigFields(item.items))],
                    });
                    break;

                case 'text':
                    field = new Text({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: item.value,
                        flex: 1,
                    });
                    break;

                case 'color':
                    field = new Color({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: item.value,
                        flex: 1,
                    });
                    break;

                case 'select':
                    field = new Select({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: item.value,
                        store: new Local({
                            provider: {
                                dataFields: ['value', 'display'],
                                datas: ((options: { [key: string]: any }) => {
                                    const datas = [];
                                    for (const value in options) {
                                        datas.push([value, options[value]]);
                                    }

                                    return datas;
                                })(item.options),
                            },
                        }),
                    });
                    break;

                default:
                    field = new Text({
                        name: item.name,
                        label: item.label ?? 'UNKNOWN',
                        value: typeof item.value == 'object' ? JSON.stringify(item.value) : item.value,
                        helpText: Language.getError('UNSUPPORTED_FIELD_TYPE', { type: item.type }),
                    });
                    break;
            }

            if (field !== null) {
                fields.push(field);
            }
        }

        return fields;
    }

    /**
     * 선택된 테마의 추가적인 정보를 가져온다.
     *
     * @param key - 가져올 키
     * @returns 데이터
     */
    async getData(key: string): Promise<any> {
        const rawValue = await this.getSelect().getValueToRecordOne();
        return rawValue?.get(key) ?? null;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(
        value: string | { name: string; config: object | null },
        is_init: boolean = false,
        is_update: boolean = true
    ): Promise<void> {
        await this.lockValue();

        if (typeof value == 'string') {
            value = { name: value, config: null };
        }

        if (is_update == true) {
            await this.getSelect().setValue(value.name);
            await this.getConfig().setValue(value.config);
        }

        await this.releaseValue(await this.getValue(), is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<{ name: string; config: object | null }> {
        await this.#changeReady;

        const name = (await this.getSelect().getValue()) ?? null;
        if (name !== null) {
            return { name: name, config: ((await this.getConfig().getValue()) ?? null) as object | null };
        }

        return null;
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getSelect().$getComponent());
        await this.getSelect().render();

        this.$getContent().append(this.getConfig().$getComponent());
        await this.getConfig().render(true);
    }
}
